/**
 * Pagina Inbox principale: 3 pannelli + ricerca + AI search.
 * v2: aggiunto polling automatico ogni 15s come fallback se WebSocket
 * non funziona, e auto-correzione del selectedAccountId se nullo.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, Sparkles, X, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { MessageList } from '@/components/inbox/MessageList';
import { MessageViewer } from '@/components/inbox/MessageViewer';
import { ShortcutsHelp } from '@/components/shortcuts/ShortcutsHelp';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useInbox } from '@/stores/inbox.store';
import { useComposer } from '@/stores/composer.store';
import { messagesApi, aiApi, threadsApi, accountsApi } from '@/lib/api/endpoints';
import { useKeyboardShortcuts } from '@/lib/hooks/useKeyboardShortcuts';

export function InboxPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { selectedAccountId, selectedFolderRole, selectedCategory, selectedLabel, selectedUrgency,
          unreadOnly, starredOnly, selectedMessageId, selectMessage, setAccount } = useInbox();

  const openCompose = useComposer((s) => s.openCompose);
  const [searchQuery, setSearchQuery] = useState('');
  const [aiSearchActive, setAiSearchActive] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);

  // Filter starred from query string
  const starredFromUrl = searchParams.get('starred') === '1';
  const effectiveStarred = starredOnly || starredFromUrl;

  // FIX: se selectedAccountId è null all'avvio, fetch accounts e prendi il primo
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
  });

  useEffect(() => {
    const accounts = accountsQuery.data?.accounts;
    if (!accounts || accounts.length === 0) return;
    // L'id selezionato e persistito nel browser: se punta a un account
    // eliminato/ricollegato va sostituito, altrimenti tutte le liste
    // interrogano un account inesistente e restano vuote.
    const isValid = !!selectedAccountId && accounts.some((a) => a.id === selectedAccountId);
    if (!isValid) {
      const def = accounts.find((a) => a.is_default) ?? accounts[0];
      if (def) setAccount(def.id);
    }
  }, [selectedAccountId, accountsQuery.data, setAccount]);

  // Lista messaggi - con polling e refetch on focus
  const messagesQuery = useQuery({
    queryKey: ['messages', selectedAccountId, selectedFolderRole, selectedCategory, selectedLabel, selectedUrgency, unreadOnly, effectiveStarred],
    queryFn: () =>
      messagesApi.list({
        accountId: selectedAccountId!,
        folderRole: selectedFolderRole,
        limit: 50,
        category: selectedCategory ?? undefined,
        label: selectedLabel ?? undefined,
        urgency: selectedUrgency ?? undefined,
        unreadOnly,
        starredOnly: effectiveStarred,
      }),
    enabled: !!selectedAccountId,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  // Ricerca AI
  const aiSearchQuery = useQuery({
    queryKey: ['ai-search', selectedAccountId, searchQuery, aiSearchActive],
    queryFn: () =>
      aiApi.search({
        accountId: selectedAccountId ?? undefined,
        query: searchQuery,
        limit: 30,
        synthesize: true,
      }),
    enabled: !!selectedAccountId && aiSearchActive && searchQuery.length > 2,
  });

  const messages = useMemo(
    () =>
      aiSearchActive && aiSearchQuery.data
        ? aiSearchQuery.data.results
        : messagesQuery.data?.messages ?? [],
    [aiSearchActive, aiSearchQuery.data, messagesQuery.data]
  );

  // Keyboard navigation
  function nextMessage() {
    if (messages.length === 0) return;
    const idx = messages.findIndex((m) => m.id === selectedMessageId);
    const next = messages[Math.min(idx + 1, messages.length - 1)];
    if (next) selectMessage(next.id);
  }
  function prevMessage() {
    if (messages.length === 0) return;
    const idx = messages.findIndex((m) => m.id === selectedMessageId);
    const prev = messages[Math.max(idx - 1, 0)];
    if (prev) selectMessage(prev.id);
  }

  function reply() {
    const msg = messages.find((m) => m.id === selectedMessageId);
    if (!msg) return;
    openCompose({
      threadId: msg.thread_id,
      inReplyToMessageId: msg.id,
      to: msg.from_address ? [{ name: msg.from_address.name, address: msg.from_address.email }] : [],
      subject: `Re: ${msg.subject || ''}`.replace(/^Re: Re: /, 'Re: '),
    });
  }

  async function archiveSelected() {
    const msg = messages.find((m) => m.id === selectedMessageId);
    if (!msg) return;
    await threadsApi.archive(msg.thread_id);
    nextMessage();
  }
  async function trashSelected() {
    const msg = messages.find((m) => m.id === selectedMessageId);
    if (!msg) return;
    await threadsApi.trash(msg.thread_id);
    nextMessage();
  }
  async function starSelected() {
    const msg = messages.find((m) => m.id === selectedMessageId);
    if (!msg) return;
    const next = !(msg.is_starred || msg.thread_starred);
    await threadsApi.star(msg.thread_id, next);
  }

  useKeyboardShortcuts({
    onNext: nextMessage,
    onPrev: prevMessage,
    onReply: reply,
    onArchive: () => void archiveSelected(),
    onTrash: () => void trashSelected(),
    onStar: () => void starSelected(),
    onShowHelp: () => setShowHelp(true),
    onFocusSearch: () => searchInputRef.current?.focus(),
  });

  const isLoading = messagesQuery.isLoading || aiSearchQuery.isFetching;
  const folderTitle: Record<string, string> = {
    inbox: 'Inbox',
    sent: 'Inviati',
    drafts: 'Bozze',
    trash: 'Cestino',
    spam: 'Spam',
    archive: 'Archivio',
  };
  const title = selectedLabel
    ? `Etichetta: ${selectedLabel}`
    : selectedCategory
    ? `Categoria: ${selectedCategory}`
    : effectiveStarred
    ? 'Speciali'
    : folderTitle[selectedFolderRole] ?? 'Inbox';

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />

      <div className="flex-1 flex min-w-0">
        {/* List pane */}
        <div className="w-[420px] border-r flex flex-col bg-background min-w-0">
          {/* Search */}
          <div className="p-3 border-b">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setAiSearchActive(searchQuery.length > 2);
              }}
              className="relative"
            >
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Cerca con AI... (premi /)"
                className="pl-9 pr-9"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (e.target.value.length === 0) setAiSearchActive(false);
                }}
              />
              {searchQuery.length > 0 && (
                <button
                  type="button"
                  onClick={() => { setSearchQuery(''); setAiSearchActive(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </form>
          </div>

          {/* AI synthesis */}
          <AnimatePresence>
            {aiSearchActive && aiSearchQuery.data?.synthesis && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-primary/5 border-b text-sm"
              >
                <div className="p-3">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-primary mb-2">
                    <Sparkles className="h-3 w-3" /> Risposta AI
                  </div>
                  <p className="leading-relaxed">{aiSearchQuery.data.synthesis}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Header */}
          <div className="px-4 py-2 border-b text-xs text-muted-foreground flex items-center justify-between">
            <span>
              {title} - {messages.length} messaggi
            </span>
            {(selectedCategory || selectedUrgency || selectedLabel) && (
              <button
                onClick={() => {
                  navigate('/');
                  useInbox.getState().resetFilters();
                }}
                className="hover:text-foreground inline-flex items-center gap-1"
              >
                <X className="h-3 w-3" /> Pulisci filtri
              </button>
            )}
          </div>

          <div ref={messageListRef} className="flex-1 overflow-hidden flex flex-col">
            <MessageList
              messages={messages}
              selectedId={selectedMessageId}
              onSelect={selectMessage}
              loading={isLoading}
            />
          </div>
        </div>

        {/* Detail pane */}
        {selectedMessageId ? (
          <MessageViewer
            messageId={selectedMessageId}
            accountId={selectedAccountId!}
            onClose={() => selectMessage(null)}
          />
        ) : (
          <EmptyDetailPane />
        )}
      </div>

      <ShortcutsHelp open={showHelp} onOpenChange={setShowHelp} />
    </div>
  );
}

function EmptyDetailPane() {
  return (
    <div className="flex-1 flex items-center justify-center text-muted-foreground bg-muted/10">
      <div className="text-center max-w-md px-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <Sparkles className="h-8 w-8 text-primary" />
        </div>
        <div className="text-lg font-medium text-foreground mb-1">Pronto a iniziare</div>
        <div className="text-sm">
          Seleziona un messaggio per leggerlo, oppure premi{' '}
          <kbd className="inline-flex items-center justify-center h-5 px-1.5 rounded border bg-muted text-[11px] font-mono">
            C
          </kbd>{' '}
          per scrivere.
        </div>
        <div className="text-xs mt-3">
          Premi <kbd className="inline-flex items-center justify-center h-5 px-1.5 rounded border bg-muted text-[11px] font-mono">?</kbd>{' '}
          per le scorciatoie da tastiera.
        </div>
      </div>
    </div>
  );
}
